import { Link, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/Seo";
import { LEGAL_DOCS } from "@/lib/legalContent";
import { BUSINESS, LEGAL_LINKS } from "@/lib/constants";
import { breadcrumbSchema } from "@/lib/schema";
import { Reveal } from "@/components/Reveal";

export default function LegalPage({ slug }) {
  const params = useParams();
  const doc = LEGAL_DOCS[slug || params.slug];

  if (!doc) {
    return (
      <PageShell testid="legal-not-found" title="Page not found" breadcrumbs={[{ label: "Policies" }]}>
        <section className="py-16 px-4 sm:px-6 max-w-3xl mx-auto">
          <p className="text-sm text-spice-muted">
            The policy you are looking for is not available. Please use the links in the footer.
          </p>
        </section>
      </PageShell>
    );
  }

  return (
    <>
      <Seo
        title={doc.metaTitle}
        description={doc.metaDescription}
        path={`/${doc.slug}`}
        jsonLd={breadcrumbSchema([{ name: doc.title, url: `/${doc.slug}` }])}
      />
      <PageShell
        testid={`legal-page-${doc.slug}`}
        title={doc.title}
        subtitle={doc.intro}
        breadcrumbs={[{ label: doc.title }]}
      >
        <section className="py-12 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-4 gap-10">
            <div className="lg:col-span-3 space-y-7">
              {BUSINESS.legalReviewPending ? (
                <div className="p-4 bg-white border-l-4 border-spice-gold rounded text-xs text-spice-muted space-y-1" data-testid="legal-review-notice">
                  <strong className="text-spice-ink block">Draft — pending owner and legal review.</strong>
                  This policy has been drafted for our quotation-based wholesale business and is awaiting review and
                  approval by the proprietor before final publication.
                </div>
              ) : (
                <p className="text-xs text-spice-subtle" data-testid="legal-updated-date">
                  Approved and published on {BUSINESS.legalUpdated}. Last updated: {BUSINESS.legalUpdated}.
                </p>
              )}
              {doc.sections.map((section) => (
                <Reveal key={section.heading} className="space-y-2">
                  <h2 className="text-base font-bold text-spice-ink">{section.heading}</h2>
                  {section.body.map((para) => (
                    <p key={para.slice(0, 32)} className="text-sm text-spice-muted leading-relaxed">{para}</p>
                  ))}
                </Reveal>
              ))}
            </div>

            <aside className="lg:col-span-1">
              <div className="bg-white border border-spice-line rounded-lg p-5 space-y-3 sticky top-28" data-testid="legal-sidebar">
                <h2 className="text-xs font-bold uppercase tracking-widest text-spice-green">Policies</h2>
                <nav className="flex flex-col gap-2 text-xs">
                  {LEGAL_LINKS.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`hover:text-spice-green transition-colors ${
                        link.to === `/${doc.slug}` ? "text-spice-green font-semibold" : "text-spice-muted"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>
          </div>
        </section>
      </PageShell>
    </>
  );
}
